import { useContext } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { AuthContext } from '@/context/auth-context'
import { AnalyticsEvent, AnalyticsEventProperties } from '@/analytics/schema/events.schema.ts'
import { DataLayerObject } from '@/analytics/schema/tag-manager.schema.ts'
import { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import Cookies from 'js-cookie'
import { useParams } from 'react-router-dom'
import { setTrackEventGlobal } from '@/analytics/utils/analytics-helpers'
import { kAnalyticsFeatureAgent } from '@/analytics/constants'
import { kRouteSegments } from '@/routing/paths'

// Keys that should always persist between events and never be cleaned
const GLOBAL_KEYS = new Set<keyof DataLayerObject>([
  'event',
  'userEmail',
  'userId',
  'activeMatterId',
  'subscriptionStatus',
  'orgSubscriptionStatus',
  'hubspotUtk',
  'transaction_id',
])

// Snapshot of keys sent in the previous event (excluding GLOBAL_KEYS)
let lastVolatileKeys: string[] = []

// Reusable type for the trackEvent callback
export type TrackEventFn = <T extends AnalyticsEvent>(
  event: T,
  properties?: (AnalyticsEventProperties[T] & Record<string, string | number | boolean>) | undefined,
  extraOpts?: { suppressFeatureInheritance?: boolean }
) => void

declare global {
  interface Window {
    dataLayer: Array<DataLayerObject>
    trackEventGlobal?: TrackEventFn
  }
}

const getHubSpotTrackingCookie = (): string | undefined => {
  return Cookies.get('hubspotutk')
}

export const useAnalytics = () => {
  const { user, userAccountData } = useContext(AuthContext)
  const { chatFeature } = useParams()
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  // Grab HubSpot tracking cookie
  const hubspotUtk = getHubSpotTrackingCookie()

  const trackEvent = <T extends AnalyticsEvent>(
    event: T,
    properties?: Omit<AnalyticsEventProperties[T], 'userId' | 'subscriptionStatus' | 'orgSubscriptionStatus'> & Record<string, string | number | boolean>,
    extraOpts: { suppressFeatureInheritance?: boolean } = {}
  ) => {
    const transaction_id = uuidv4()
    window.dataLayer = window.dataLayer || []

    // Normalize the feature name for analytics (e.g. assistant → agent)
    const normalizedFeature = extraOpts.suppressFeatureInheritance ? undefined : chatFeature === kRouteSegments.assistant ? kAnalyticsFeatureAgent : undefined

    const eventData: DataLayerObject = {
      event,
      userEmail: user?.email || '',
      userId: user?.uid || '',
      activeMatterId: activeMatterId || '',
      subscriptionStatus: userAccountData?.subscriptionStatus,
      ...properties,
      feature: normalizedFeature || '',
      hubspotUtk,
      transaction_id,
    }

    // --- Dynamic cleaning of inherited properties ---
    // Add empty string for any key that existed in the previous event but is missing now
    lastVolatileKeys.forEach((k) => {
      if (!(k in eventData)) {
        ;(eventData as unknown as Record<string, unknown>)[k] = ''
      }
    })

    // If orgData exists and has subscription_status, we add it
    if (userAccountData?.orgData?.subscription_status) {
      eventData.orgSubscriptionStatus = userAccountData.orgData.subscription_status
    }

    window.dataLayer.push(eventData)

    // Update snapshot of volatile keys for next event
    lastVolatileKeys = Object.keys(eventData).filter((k) => !GLOBAL_KEYS.has(k as keyof DataLayerObject))
  }

  // Expose globally for use in non-React contexts (Redux listeners, WebSocket handlers, etc.)
  setTrackEventGlobal(trackEvent)

  return { trackEvent }
}
